import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { AuthService } from '@spartacus/core';
import { BehaviorSubject, Observable, combineLatest, of, zip } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { ArticleService } from '../../../core/catalog';
import { AccountingCodeList, Article, CartError, CartType, OrderEntry, Price, Unit } from '../../../core/model';
import { OrderCardsFacade, PrincipalConfigurationService, StockInfoFacade } from '../../../core/user';
import { matchEntry } from '../../utils';
import { getTextToDisplayInsteadOfPrice } from '../../utils/price-utility';
import { allWarehousesHaveZeroStock } from '../../utils/stock-utils';
import { ORDER_ENTRY_CONFIG, OrderEntryConfig, UpdateQuantityEvent } from '../order-entry';

interface TableData {
  key: string;
  orderEntry: OrderEntry;
  article$: Observable<Article>;
  loading$: Observable<boolean>;
  selected$: Observable<boolean>;
  similarArticles$: Observable<Article[]> | null;
}

export interface UpdateEntryQuantityEvent {
  entry: OrderEntry;
  quantity: number;
  unit: Unit;
}

export interface UpdateEntryNoteEvent {
  entry: OrderEntry;
  note: string | null;
}

export interface UpdateEntryStatisticsCodeEvent {
  entry: OrderEntry;
  code: string | null;
}

export interface UpdateStatisticsCodeEvent {
  entry: OrderEntry;
  code: string | null;
}

export interface RemoveEntryEvent {
  entry: OrderEntry;
}

export interface SelectEntryEvent {
  entry: OrderEntry;
}

export interface ReplaceEntryEvent {
  entry: OrderEntry;
  replacementArticle: Article;
}

export interface AddToCartEntryEvent {
  entry: OrderEntry;
  article: Article;
}

interface CombinedSampleProduct {
  code: string;
  quantity: number;
}

@Component({
  selector: 'py-cart-entries-table',
  templateUrl: './cart-entries-table.component.html',
  styleUrls: ['./cart-entries-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CartEntriesTableComponent implements OnInit, OnChanges {
  readonly cartTypesWithVisibleConsignmentStockLevel = [CartType.consignmentStock, CartType.consignmentStockFillup];

  @Input() set loading(loading: boolean) {
    this.loading$.next(loading);
  }
  @Input() set enableSimilarArticles(enableSimilarArticles: boolean) {
    this.enableSimilarArticles$.next(enableSimilarArticles);
  }
  @Input() set entries(entries: OrderEntry[] | undefined) {
    this.entries$.next(entries || []);
  }
  @Input() set selectedEntries(selectedEntries: OrderEntry[]) {
    this.selectedEntries$.next(selectedEntries ?? []);
  }

  @Input() totalPrice?: Price;
  @Input() numberOfLoadingEntries?: number;
  @Input() enableEditEntryAndShowingSimilar?: boolean = true;
  @Input() isCartWriter: boolean;
  @Input() formGroup: UntypedFormGroup;
  @Input() accountingCodeList$: Observable<AccountingCodeList>;
  @Input() accountingCodeListLoading: boolean;
  @Input() cartType: CartType;
  @Input() cartErrors: CartError[] | null;

  @Output() selectEntry = new EventEmitter<SelectEntryEvent>();
  @Output() selectAll = new EventEmitter<void>();
  @Output() updateEntry = new EventEmitter<UpdateEntryQuantityEvent>();
  @Output() updateEntryNote = new EventEmitter<UpdateEntryNoteEvent>();
  @Output() updateEntryStatisticsCode = new EventEmitter<UpdateStatisticsCodeEvent>();
  @Output() removeEntry = new EventEmitter<RemoveEntryEvent>();
  @Output() replaceEntry = new EventEmitter<ReplaceEntryEvent>();
  @Output() addToCartEntry = new EventEmitter<AddToCartEntryEvent>();

  @ViewChildren('orderEntryRows') orderEntryRows: QueryList<any>;
  @ViewChildren('orderEntryLists') orderEntryLists: QueryList<any>;

  tableData$: Observable<TableData[]>;
  isLoadingWithoutData$: Observable<boolean>;
  isAllSelected$: Observable<boolean>;
  isOrderCardsUserEnabled$: Observable<Boolean>;

  sampleProductsLimit$: Observable<number>;
  sampleEntriesCount: number = 0;
  maxA4SampleProductsPerOrder: number;
  maxA4SampleSheetsPerProduct: number;
  sampleProductsLimits$ = new BehaviorSubject<Map<string, number>>(new Map<string, number>());

  enableSecondaryArticleRowVariant$: Observable<boolean>;
  hasDiscontinuedEntry: boolean = false;
  textInsteadOfPrice: string | undefined;

  private lastUpdatedKey$ = new BehaviorSubject<string | null>(null);
  private enableSimilarArticles$ = new BehaviorSubject<boolean>(true);
  private loading$ = new BehaviorSubject<boolean>(false);
  private entries$ = new BehaviorSubject<OrderEntry[]>([]);
  private selectedEntries$ = new BehaviorSubject<OrderEntry[]>([]);

  get priceColumnIndex(): number {
    return this.config.selectable ? 4 : 3;
  }

  get isConsignmentStockLevelVisible(): boolean {
    return this.cartTypesWithVisibleConsignmentStockLevel.includes(this.cartType);
  }

  constructor(
    @Inject(ORDER_ENTRY_CONFIG) public config: OrderEntryConfig,
    private articleService: ArticleService,
    private authService: AuthService,
    private orderCardsService: OrderCardsFacade,
    private principalConfigurationService: PrincipalConfigurationService,
    private stockInfoService: StockInfoFacade
  ) {}

  ngOnInit(): void {
    this.initTableData();
    this.isLoadingWithoutData$ = combineLatest([this.entries$, this.loading$]).pipe(
      map(([entries, loading]) => loading && entries.length === 0),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    const numberOfSelectableEntries$: Observable<number> = this.tableData$.pipe(
      switchMap((tableData) => zip(...tableData.map(({ article$ }) => article$))),
      map((articles) => articles.filter((article) => !this.articleService.isArticleDiscontinued(article))),
      map((articles) => articles.length)
    );
    this.isAllSelected$ = combineLatest([numberOfSelectableEntries$, this.selectedEntries$]).pipe(
      map(
        ([numberOfSelectableEntries, selectedEntries]) =>
          numberOfSelectableEntries > 0 && selectedEntries?.length === numberOfSelectableEntries
      )
    );

    this.loading$.subscribe((loading) => {
      if (!loading) {
        this.lastUpdatedKey$.next(null);
      }
    });

    this.isOrderCardsUserEnabled$ = this.orderCardsService
      .isOrderCardsUserEnabled()
      .pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

    this.enableSecondaryArticleRowVariant$ = this.principalConfigurationService
      .isEnabled('enableSecondaryArticleRowVariant')
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.sampleProductsLimit$ = combineLatest([
      this.principalConfigurationService.getValue('maxA4SampleProductsPerOrder'),
      this.principalConfigurationService.getValue('maxA4SampleSheetsPerProduct'),
    ]).pipe(
      map(([maxA4SampleProductsPerOrder, maxA4SampleSheetsPerProduct]) => {
        this.maxA4SampleProductsPerOrder = maxA4SampleProductsPerOrder?.value as number;
        this.maxA4SampleSheetsPerProduct = maxA4SampleSheetsPerProduct?.value as number;
        return this.maxA4SampleSheetsPerProduct;
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.cartType) {
      if (changes.cartType.currentValue === CartType.sample) {
        this.initTableData();
      }

      this.textInsteadOfPrice = getTextToDisplayInsteadOfPrice(changes.cartType.currentValue);
    }
  }

  initTableData(): void {
    const tableData$ = combineLatest([
      this.entries$.pipe(filter((entries) => Boolean(entries))),
      this.enableSimilarArticles$.pipe(distinctUntilChanged()),
      this.principalConfigurationService.isEnabled('enableDisplayAlternativeArticles'),
    ]).pipe(
      map(([entries, enableSimilarArticles, isDisplayAlternativeArticlesEnabled]) => {
        // this block is to combine the same sample products with different variants (e.g. different weights) in map of (productCode, quantity)
        const sampleProductsLimits = new Map();

        entries?.forEach((entry) => {
          const combinedSampleProduct: CombinedSampleProduct = entries
            ?.filter((orderEntry) => orderEntry.a4SampleProduct?.code === entry.a4SampleProduct?.code)
            ?.reduce((acc, curr) => ({ code: curr.a4SampleProduct?.code, quantity: curr.quantity + acc.quantity }), {
              quantity: 0,
            }) as CombinedSampleProduct;

          if (!sampleProductsLimits.get(combinedSampleProduct.code)) {
            sampleProductsLimits.set(combinedSampleProduct.code, combinedSampleProduct.quantity);
          }
        });

        this.sampleProductsLimits$.next(sampleProductsLimits);

        return entries.map((orderEntry) => {
          const article$ = this.articleService.getArticle(orderEntry.articleRef).pipe(
            filter((article) => !!article),
            takeUntil(this.authService.logoutInProgress$.pipe(filter((logout) => !!logout)))
          );

          const similarArticles$ = enableSimilarArticles
            ? isDisplayAlternativeArticlesEnabled
              ? this.articleService.getSimilarArticles(orderEntry.articleRef).pipe(
                  filter((alternatives) => Boolean(alternatives)),
                  switchMap((alternatives) =>
                    alternatives.length > 0
                      ? zip(
                          ...alternatives.map((alternative) =>
                            this.articleService.getArticle(alternative.articleRef).pipe(
                              filter(Boolean),
                              switchMap((article: Article) => {
                                //For alternative items we want to show only in-stock products
                                return this.stockInfoService
                                  .getStockInfo(article.code, 1)
                                  .pipe(map((stockInfo) => (allWarehousesHaveZeroStock(stockInfo) ? null : article)));
                              })
                            )
                          )
                        )
                      : of([])
                  ),
                  map((articles: Article[]) =>
                    articles.filter(
                      (article) => Boolean(article) && article.unit?.code != null && article.minimumOrderQuantity != null
                    )
                  )
                )
              : of([])
            : null;
          const key = orderEntry.id;
          return {
            key,
            orderEntry,
            article$,
            loading$: this.lastUpdatedKey$.pipe(map((lastUpdatedKey) => lastUpdatedKey === key)),
            selected$: this.selectedEntries$.pipe(
              map((selectedEntries) => selectedEntries.some((selectedEntry) => matchEntry(selectedEntry, orderEntry)))
            ),
            similarArticles$,
          };
        });
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.tableData$ = tableData$;
  }

  /* For type coercion in template */
  asTableData = (entry: any): TableData => entry;

  onEntryDelete({ orderEntry }: TableData): void {
    this.removeEntry.emit({ entry: orderEntry });
  }

  onEntryReplace({ orderEntry }: TableData, replacementArticle: Article): void {
    this.replaceEntry.emit({ entry: orderEntry, replacementArticle });
  }

  onEntryAddToCart({ orderEntry }: TableData, article: Article): void {
    this.addToCartEntry.emit({ entry: orderEntry, article });
  }

  onEntrySelect({ orderEntry }: TableData): void {
    this.selectEntry.emit({ entry: orderEntry });
  }

  onAllSelect(): void {
    this.selectAll.emit();
  }

  onEntryNoteUpdate({ orderEntry }: TableData, note: string | null): void {
    this.updateEntryNote.emit({ entry: orderEntry, note });
  }

  onEntryStatisticsCodeUpdate({ orderEntry }: TableData, code: string | null): void {
    this.updateEntryStatisticsCode.emit({ entry: orderEntry, code });
  }

  onEntryQuantityUpdate({ key, orderEntry }: TableData, event: UpdateQuantityEvent) {
    this.lastUpdatedKey$.next(key);
    const unit: Unit = event.unitCode ? { code: event.unitCode } : orderEntry.unit;
    this.updateEntry.emit({ entry: orderEntry, quantity: event.quantity, unit });
  }

  public markAsDirty(): void {
    this.orderEntryRows?.toArray().forEach((row) => row.markAsDirty());
    this.orderEntryLists?.toArray().forEach((list) => list.markAsDirty());
  }

  public onAccountingCodeUpdated(): void {
    this.markAsDirty();
  }
}
